/**
 * Portal app entry (`/portal/:appSlug`) — resolves an app's REAL home
 * page and redirects there. The home page's slug is arbitrary (the `is_home`
 * page; e.g. "dashboard"), so we read it from the public branding endpoint
 * rather than assuming a literal "home". This is the single place that knows
 * how to find an app's home — the launcher, the app switcher, and the per-app
 * login all route through here.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";

import { portalApi } from "@/lib/api-portal";
import { portalAssistantPath, portalPagePath } from "@/lib/portalAuth";

export function PortalAppEntry() {
  const { appSlug = "" } = useParams();

  const brandingQ = useQuery({
    queryKey: ["portal-branding", appSlug],
    queryFn: () => portalApi.getBranding(appSlug),
    staleTime: 5 * 60_000,
  });

  if (brandingQ.isPending) {
    return (
      <div className="tray-light flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Resolve the app's front door (ADR-0049): a web home page wins; else a
  // page-less app opens its autonomous assistant (chat → voce); else fall back
  // to the literal "home" slug (apps that predate the branding home field).
  // The home is a STANDALONE surface, so gate on `*_standalone` (an embed-only
  // assistant has no front door of its own) — NOT the widget flag `*_enabled`.
  const b = brandingQ.data;
  if (b?.home_slug) return <Navigate to={portalPagePath(appSlug, b.home_slug)} replace />;
  if (b?.chat_standalone) return <Navigate to={portalAssistantPath(appSlug, "chat")} replace />;
  if (b?.voice_standalone) return <Navigate to={portalAssistantPath(appSlug, "voice")} replace />;
  return <Navigate to={portalPagePath(appSlug, "home")} replace />;
}
